import { SchemaHelperView } from '../view/SchemaHelperView';
import { templateSchemaHelper } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../../framework/BaseWebComponentController';
import { SchemaHelperWebComponent } from '../index';
import SchemaHelperRepository from '../repository/SchemaHelperRepository';
import { debounce } from '../../../../utils/event.utils';
import { SchemaTable } from '../types';
import { EnumListEvents } from '../../../../ui/gzEnumList/types';

export class SchemaHelperController extends BaseWebComponentController {
  public component: SchemaHelperWebComponent;
  public view: SchemaHelperView;
  public viewConstructor: typeof SchemaHelperView;
  public dataRepository: SchemaHelperRepository;
  private data: Array<SchemaTable>;
  private searchKey = '';
  private osFilter: Array<string> | null = null;

  constructor(component: SchemaHelperWebComponent) {
    super(component);

    this.initialSetup({
      dataRepositoryConstructor: SchemaHelperRepository,
      viewConstructor: SchemaHelperView,
      viewTemplate: templateSchemaHelper,
    });
  }

  public async render(): Promise<void> {
    const result = await this.dataRepository.getDataForView();
    this.data = result.data;
    this.view.render(result);
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$inputSearch,
        event: 'change',
        callback: debounce(this.handleSearchInputChange, 700),
      },
      {
        element: this.view.dom.$osFilter,
        event: EnumListEvents.SELECTION_CHANGE,
        callback: this.handleSearchInputChange,
      },
    ];
  }

  private async handleSearchInputChange(ev: CustomEvent): Promise<void> {
    if (ev.type === 'change') {
      this.searchKey = ev.detail.sourceEvent;
    } else if (ev.type === EnumListEvents.SELECTION_CHANGE) {
      this.osFilter = ev.detail.selectedValues;
    }

    if (this.searchKey.replace(/\s+/g, '') === '' || this.searchKey.length < 2) {
      this.searchKey = '';
    }

    const [filteredResults, indexesOfOpenAccordions] = this.filter();

    this.view.renderSchemaHelper(filteredResults, this.searchKey, indexesOfOpenAccordions);
  }

  private filter(): [Array<SchemaTable>, Array<number>] {
    const filteredBySearchKey: SchemaTable[] = this.filterBySearch();
    const filteredByOS: SchemaTable[] = this.filterByOS(filteredBySearchKey);
    const indexesOfOpenAccordions: Array<number> = this.findIndexesOfOpenAccordions(filteredByOS);

    return [filteredByOS, indexesOfOpenAccordions];
  }

  private filterBySearch(): Array<SchemaTable> {
    return this.data.filter(data => {
      const textsToSearchIn: Array<string> = [data.name];

      data.columns.forEach(column => {
        textsToSearchIn.push(column.name);
      });

      return this.view.areAllWordsFromSearchTextIncludedInArray(textsToSearchIn, this.searchKey);
    });
  }

  private filterByOS(filteredData: Array<SchemaTable>): Array<SchemaTable> {
    if (this.osFilter === null) {
      return filteredData;
    }

    return filteredData.filter(
      data => data.platforms.filter(platform => this.osFilter !== null && this.osFilter.includes(platform)).length !== 0
    );
  }

  //if any of the search words are included in columns - add the index to opened accordion list
  private findIndexesOfOpenAccordions(filteredData: Array<SchemaTable>): Array<number> {
    const indexesOfOpenAccordions: Array<number> = [];

    if (this.searchKey === '') {
      return indexesOfOpenAccordions;
    }
    const lowercaseSearchWords = this.searchKey.toLowerCase().split(/\s+/);
    filteredData.forEach((data, index) => {
      data.columns.forEach(column => {
        for (const lowercaseSearchWord of lowercaseSearchWords) {
          if (column.name.toLowerCase().includes(lowercaseSearchWord)) {
            indexesOfOpenAccordions.push(index);
            return;
          }
        }
      });
    });

    return indexesOfOpenAccordions;
  }
}
